/**
 * DesignTree — the render page's Designs panel as a directory tree over the staged
 * designs (built from their relative paths). Folders cascade (tri-state); ticking a
 * file/folder marks it to RENDER. The ✕ drops designs from the batch entirely.
 */
import { useMemo, useState } from "react";
import type { DesignInput } from "@genz/platform";
import {
  buildDesignTree,
  fileIdsUnder,
  folderState,
  type FolderNode,
  type TreeNode,
} from "./designTree.js";
import { Icon } from "../ui/Icon.js";

export interface DesignTreeProps {
  designs: DesignInput[];
  /** Currently render-selected design ids. */
  selected: Set<string>;
  /** Toggle a file (one id) or a folder (many ids) on/off. */
  onToggle(ids: string[], on: boolean): void;
  /** Drop these designs from the batch (✕). */
  onRemove(ids: string[]): void;
  /** Search text — filters files by name and auto-expands matching folders. */
  filter?: string;
}

/** Keep only files whose name matches `q` (and the folders on their path). Returns null
 *  when nothing under this folder matches. */
function pruneTree(node: FolderNode, q: string): FolderNode | null {
  const children: TreeNode[] = [];
  for (const child of node.children) {
    if (child.kind === "file") {
      if (
        child.name.toLowerCase().includes(q) ||
        (child.code ?? "").toLowerCase().includes(q)
      ) {
        children.push(child);
      }
    } else {
      const kept = pruneTree(child, q);
      if (kept) children.push(kept);
    }
  }
  return children.length > 0 ? { ...node, children } : null;
}

/** Tri-state checkbox that reflects `indeterminate` via a ref callback. */
function TriCheck({
  state,
  onChange,
  testid,
  label,
}: {
  state: "checked" | "indeterminate" | "unchecked";
  onChange(): void;
  testid: string;
  label: string;
}) {
  return (
    <input
      type="checkbox"
      className="design-tree__check"
      checked={state === "checked"}
      ref={(el) => {
        if (el) el.indeterminate = state === "indeterminate";
      }}
      onChange={onChange}
      data-testid={testid}
      aria-label={label}
    />
  );
}

function Rows({
  nodes,
  depth,
  selected,
  expanded,
  toggleExpand,
  forceOpen,
  onToggle,
  onRemove,
}: {
  nodes: TreeNode[];
  depth: number;
  selected: Set<string>;
  expanded: Set<string>;
  toggleExpand(path: string): void;
  forceOpen: boolean;
  onToggle: DesignTreeProps["onToggle"];
  onRemove: DesignTreeProps["onRemove"];
}) {
  return (
    <>
      {nodes.map((node) => {
        const pad = { paddingLeft: depth * 14 } as const;
        if (node.kind === "file") {
          const on = selected.has(node.designId);
          return (
            <li key={`f:${node.designId}`} className="design-tree__row" style={pad} data-testid="design-tree-file-row">
              <TriCheck
                state={on ? "checked" : "unchecked"}
                onChange={() => onToggle([node.designId], !on)}
                testid={`design-tree-file-check-${node.designId}`}
                label={`Create mock-up of ${node.name}`}
              />
              <span className="design-tree__icon" aria-hidden="true"><Icon name="dot" size={16} /></span>
              <span className="design-tree__name" title={node.name}>
                {node.code || node.name}
                {node.code && node.name.length > node.code.length && (
                  <span className="faint design-tree__ext">{node.name.slice(node.code.length)}</span>
                )}
              </span>
              <button
                type="button"
                className="design-tree__x"
                aria-label={`Remove ${node.name}`}
                onClick={() => onRemove([node.designId])}
                data-testid={`design-tree-file-remove-${node.designId}`}
              >
                <Icon name="close" size={16} />
              </button>
            </li>
          );
        }
        const ids = fileIdsUnder(node);
        const state = folderState(node, selected);
        const open = forceOpen || expanded.has(node.path);
        return (
          <li key={`d:${node.path}`} className="design-tree__group">
            <div className="design-tree__row design-tree__row--folder" style={pad} data-testid="design-tree-folder-row">
              <button
                type="button"
                className="design-tree__caret"
                aria-label={`${open ? "Collapse" : "Expand"} ${node.name}`}
                aria-expanded={open}
                onClick={() => toggleExpand(node.path)}
                data-testid={`design-tree-folder-toggle-${node.name}`}
              >
                {open ? <Icon name="chevron-down" size={16} /> : <Icon name="chevron-right" size={16} />}
              </button>
              <TriCheck
                state={state}
                onChange={() => onToggle(ids, state !== "checked")}
                testid={`design-tree-folder-check-${node.name}`}
                label={`Create mock-ups of all in ${node.name}`}
              />
              <span className="design-tree__icon" aria-hidden="true"><Icon name="chevron-right" size={16} /></span>
              <span className="design-tree__name design-tree__name--folder" title={node.name}>{node.name}</span>
              <span className="design-tree__count">{ids.length}</span>
              <button
                type="button"
                className="design-tree__x"
                aria-label={`Remove ${node.name}`}
                onClick={() => onRemove(ids)}
                data-testid={`design-tree-folder-remove-${node.name}`}
              >
                <Icon name="close" size={16} />
              </button>
            </div>
            {open && (
              <ul className="design-tree__children">
                <Rows
                  nodes={node.children}
                  depth={depth + 1}
                  selected={selected}
                  expanded={expanded}
                  toggleExpand={toggleExpand}
                  forceOpen={forceOpen}
                  onToggle={onToggle}
                  onRemove={onRemove}
                />
              </ul>
            )}
          </li>
        );
      })}
    </>
  );
}

export function DesignTree({ designs, selected, onToggle, onRemove, filter }: DesignTreeProps) {
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set());
  const q = (filter ?? "").trim().toLowerCase();

  const tree = useMemo(() => buildDesignTree(designs), [designs]);
  const shown = useMemo(() => (q ? pruneTree(tree, q) : tree), [tree, q]);

  const toggleExpand = (path: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(path)) next.delete(path);
      else next.add(path);
      return next;
    });

  if (!shown || shown.children.length === 0) {
    return (
      <ul className="design-tree" data-testid="design-tree">
        <li className="design-tree__row design-tree__row--empty" data-testid="design-tree-noresults">
          {q ? `No designs match “${filter?.trim()}”.` : "No designs yet."}
        </li>
      </ul>
    );
  }

  return (
    <ul className="design-tree" data-testid="design-tree">
      <Rows
        nodes={shown.children}
        depth={0}
        selected={selected}
        expanded={expanded}
        toggleExpand={toggleExpand}
        forceOpen={q !== ""}
        onToggle={onToggle}
        onRemove={onRemove}
      />
    </ul>
  );
}
